import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EpcBidDecisionCockpit } from '@/components/epc/commercial-ux/EpcBidDecisionCockpit';
import {
  bidReadinessTagColor,
  buildBidDecisionReadiness,
  type BidReadinessState,
} from '@/components/epc/commercial-ux/bidDecisionReadiness';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { epcCommercialService } from '@/services/epcCommercial.service';

const { Title, Text } = Typography;

type FilterTab = 'all' | 'READY' | 'BLOCKED' | 'IN_PROGRESS';

const EpcBidDecisionsPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<FilterTab>('all');
  const [focusId, setFocusId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  const listQ = useQuery({
    queryKey: ['epc-bid-decisions'],
    queryFn: () => epcCommercialService.listBidDecisions(),
    staleTime: 15_000,
  });

  const tendersQ = useQuery({
    queryKey: ['epc-tenders'],
    queryFn: () => epcCommercialService.listTenders(),
    staleTime: 30_000,
    enabled: createOpen,
  });

  const createM = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcCommercialService.createBidDecision(values),
    onSuccess: (created) => {
      message.success('Bid decision created');
      setCreateOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-bid-decisions'] });
      if (created?.id) setFocusId(String(created.id));
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const rows = (listQ.data || []) as Record<string, unknown>[];

  const enriched = useMemo(
    () =>
      rows.map((r) => ({
        row: r,
        readiness: buildBidDecisionReadiness(r),
      })),
    [rows]
  );

  const filtered = useMemo(() => {
    if (filter === 'all') return enriched;
    return enriched.filter((e) => e.readiness.overall === filter);
  }, [enriched, filter]);

  const focusRow = useMemo(() => {
    if (!focusId) return null;
    return rows.find((r) => String(r.id) === focusId) || null;
  }, [rows, focusId]);

  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus) setFocusId(focus);
  }, [searchParams]);

  const counts = useMemo(() => {
    const c = { READY: 0, BLOCKED: 0, IN_PROGRESS: 0 };
    for (const e of enriched) c[e.readiness.overall] += 1;
    return c;
  }, [enriched]);

  const tenderOptions = ((tendersQ.data || []) as Record<string, unknown>[]).map((t) => ({
    value: String(t.id),
    label: `${t.tender_number || String(t.id).slice(0, 8)} — ${t.title || 'Tender'}`,
  }));

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Bid decisions
          </Title>
          <Text type="secondary">
            GO / NO_GO / CONDITIONAL — submit starts Approval Runtime (`epc_bid_decision`).
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New
        </Button>
      </Space>

      <Tabs
        activeKey={filter}
        onChange={(k) => setFilter(k as FilterTab)}
        items={[
          { key: 'all', label: `All (${enriched.length})` },
          { key: 'READY', label: `Ready (${counts.READY})` },
          { key: 'BLOCKED', label: `Blocked (${counts.BLOCKED})` },
          { key: 'IN_PROGRESS', label: `In progress (${counts.IN_PROGRESS})` },
        ]}
        style={{ marginBottom: 8 }}
      />

      <Card>
        <Table
          loading={listQ.isLoading}
          rowKey={(r) => String(r.row.id)}
          dataSource={filtered}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 960 }}
          onRow={(r) => ({
            onClick: () => setFocusId(String(r.row.id)),
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: 'Number', dataIndex: ['row', 'decision_number'], width: 140 },
            {
              title: 'Tender',
              dataIndex: ['row', 'tender_id'],
              width: 120,
              render: (v) =>
                v ? (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/epc/commercial/tenders/${v}`);
                    }}
                  >
                    {String(v).slice(0, 8)}
                  </Button>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Readiness',
              key: 'readiness',
              width: 130,
              render: (_: unknown, r) => (
                <Tag color={bidReadinessTagColor(r.readiness.overall as BidReadinessState)}>
                  {r.readiness.overall}
                </Tag>
              ),
            },
            {
              title: 'Outcome',
              dataIndex: ['row', 'outcome'],
              width: 120,
              render: (v) => v || '—',
            },
            {
              title: 'Status',
              dataIndex: ['row', 'status'],
              width: 140,
              render: (s: string) => <Tag>{s}</Tag>,
            },
            {
              title: 'Notes',
              dataIndex: ['row', 'notes'],
              ellipsis: true,
              render: (v) => v || '—',
            },
          ]}
        />
      </Card>

      <Drawer
        title="Bid decision cockpit"
        open={Boolean(focusRow)}
        onClose={() => {
          setFocusId(null);
          if (searchParams.has('focus')) {
            const next = new URLSearchParams(searchParams);
            next.delete('focus');
            setSearchParams(next, { replace: true });
          }
        }}
        width={Math.min(960, typeof window !== 'undefined' ? window.innerWidth - 24 : 960)}
        styles={{ body: { paddingBottom: 24 } }}
        destroyOnClose
      >
        {focusRow ? (
          <EpcBidDecisionCockpit row={focusRow} onUpdated={() => listQ.refetch()} />
        ) : null}
      </Drawer>

      <Modal
        title="New bid decision"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="Create"
        confirmLoading={createM.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ outcome: 'PENDING' }}
          onFinish={(values) => {
            createM.mutate({
              tenderId: values.tenderId,
              outcome: values.outcome,
              notes: values.notes || undefined,
              departmentId: values.department_id,
            });
          }}
        >
          <Form.Item
            name="tenderId"
            label="Tender"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={tenderOptions}
              loading={tendersQ.isLoading}
              placeholder="Select tender"
            />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item name="outcome" label="Outcome">
            <Select
              options={[
                { value: 'PENDING', label: 'Pending' },
                { value: 'GO', label: 'GO' },
                { value: 'NO_GO', label: 'NO_GO' },
                { value: 'CONDITIONAL', label: 'Conditional' },
              ]}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EpcBidDecisionsPage;
